import React, { useState, useEffect } from 'react';
import { Power, PowerOff, AlertTriangle, CheckCircle2, Receipt } from 'lucide-react';
import { puntovService } from '../../../services/api';
import Modal from './Modal';

// Activar o inactivar una máquina en Punto V (db_maquinas.status). Antes de
// inactivar avisa si la empresa debe mensualidades o si la máquina tiene
// ventas que todavía se le van a cobrar; el aviso no impide inactivarla.
const DialogoEstadoMaquina = ({ maquina, cliente, onConfirmar, onCancel }) => {
  const inactivar = String(maquina.status) === '1';
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [advertencia, setAdvertencia] = useState(null);
  const [revisando, setRevisando] = useState(inactivar);

  useEffect(() => {
    if (!inactivar) return undefined;
    let vigente = true;
    puntovService.advertenciaInactivarMaquina(cliente.base_datos, maquina.id_maquina)
      .then((res) => vigente && setAdvertencia(res.data?.advertencia || null))
      .catch(() => vigente && setAdvertencia(null))
      .finally(() => vigente && setRevisando(false));
    return () => { vigente = false; };
  }, [inactivar, cliente.base_datos, maquina.id_maquina]);

  const confirmar = async () => {
    setError('');
    setGuardando(true);
    try {
      await onConfirmar();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado');
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={inactivar ? `Inactivar ${maquina.id_maquina}` : `Activar ${maquina.id_maquina}`}
      subtitulo={`${maquina.descripcion_maq || 'Sin descripción'} · ${cliente.slug}`}
      icono={inactivar ? PowerOff : Power}
      tono={inactivar ? 'rojo' : 'verde'}
      onClose={onCancel}
      bloqueado={guardando}
      ancho="md"
      pie={(
        <>
          <button type="button" onClick={onCancel} disabled={guardando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={guardando || revisando}
            className={`px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 ${
              inactivar ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {inactivar ? <PowerOff size={15} /> : <Power size={15} />}
            {guardando ? 'Guardando…' : inactivar ? (advertencia ? 'Inactivar de todos modos' : 'Inactivar máquina') : 'Activar máquina'}
          </button>
        </>
      )}
    >
      {inactivar && revisando && (
        <p className="mb-3 text-sm text-gray-500">Revisando pagos pendientes de la máquina…</p>
      )}
      {inactivar && advertencia && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
          <p className="font-semibold flex items-center gap-1.5"><Receipt size={15} /> Tiene pagos pendientes</p>
          <p>{advertencia}</p>
        </div>
      )}
      {inactivar ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-1.5">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle size={15} /> Al inactivarla, Punto V:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>No le entrega sus posiciones a la máquina, así que no puede mostrar productos para vender.</li>
            <li>Rechaza pagos con cupón, ventas gratuitas y cambios de precio de sus posiciones.</li>
            <li>No borra nada: al activarla vuelve a funcionar igual.</li>
          </ul>
          <p className="text-xs text-red-700 pt-1">
            Las demás máquinas de {cliente.slug} siguen funcionando.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-start gap-1.5">
          <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0" />
          La máquina vuelve a recibir sus posiciones y a vender con normalidad.
        </div>
      )}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}
    </Modal>
  );
};

export default DialogoEstadoMaquina;
